/*
 * @FilePath: /mall-fe/src/hooks/useModalManager.ts
 * @Description: 统一管理弹窗相关逻辑的 Hook
 */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useStateRef } from 'reactutils';
import { checkUserHaveRead } from 'api/user';
import { ModalContextInterface } from 'libs/contexts';

const DEFAULT_SCROLLBAR_WIDTH = '15px';

/**
 * 提取宽度
 */
function extractWidth(str: string) {
  if (!str) return DEFAULT_SCROLLBAR_WIDTH;
  return str.match(/[0-9]+px/)?.[0] ?? DEFAULT_SCROLLBAR_WIDTH;
}

/**
 * 统一管理弹窗相关逻辑的 Hook
 *
 * @param isMobile - 是否为移动端
 * @returns 返回弹窗相关的状态和方法
 */
export function useModalManager(isMobile: boolean) {
  const router = useRouter();

  // 公告列表弹窗状态
  const [visibleNoticeListModal, setVisibleNoticeListModal] = useState(false);

  // 全局弹窗状态监听（用于检测是否有弹窗打开）
  const [modalContextValueRef, setModalContextValue] = useStateRef<ModalContextInterface>({
    onModal: false,
  });

  /**
   * 校验是否需要显示公告弹窗
   */
  async function checkUserHaveReadNotice() {
    const res = await checkUserHaveRead();
    if (res.data) {
      setVisibleNoticeListModal(true);
    }
  }

  /**
   * 监听路由变化，检查是否需要显示公告弹窗
   * 只要有公告弹窗，每访问一个页面都必须弹
   * 本地开发环境就不弹公告弹窗了
   */
  useEffect(() => {
    if (
      !isMobile &&
      process.env.NODE_ENV !== 'development' &&
      router.pathname !== '/pages/footer'
    ) {
      checkUserHaveReadNotice();
    }
  }, [router.pathname, isMobile]);

  /**
   * 监听 body 的 class 变化，检测是否有弹窗打开
   * 通过 MutationObserver 监听 ant-scrolling-effect class
   */
  useEffect(() => {
    function onMutations(mutations) {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const bodyOnModal = document.body.classList.contains('ant-scrolling-effect');
          if (bodyOnModal !== modalContextValueRef.current.onModal) {
            setModalContextValue({
              onModal: bodyOnModal,
              width: bodyOnModal ? extractWidth(document.body.style.width) : undefined,
            });
          }
        }
      }
    }

    const observer = window.MutationObserver && new MutationObserver(onMutations);
    observer?.observe(document.body, { attributes: true });

    return () => {
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // 公告列表弹窗
    visibleNoticeListModal,
    setVisibleNoticeListModal,

    // 全局弹窗状态
    modalContextValue: modalContextValueRef.current,
  };
}
